import React, { useState, useContext } from "react";
import { ThemeContext } from "../context/ThemeContext";
import getCurrentSkin from "../utils/getCurrentSkin";
import HeaderBar from "../components/HeaderBar";
import ProjectPostOrderLayoutWrapper from "../components/ProjectPostOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { 
  FaSearch, 
  FaFilter, 
  FaHandshake, 
  FaChevronUp, 
  FaChevronDown,
  FaDownload,
  FaPrint,
  FaEye,
  FaTrophy,
  FaCalendarAlt,
  FaCheckCircle,
  FaAward
} from "react-icons/fa";

const TenderSuccessfulReport = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  // State to toggle filter form visibility
  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Filter state
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    contractName: "",
    tenderRefNo: "",
    minValue: "",
    maxValue: "",
    status: "successful"
  });

  // Sample data for the tender successful report
  const tenderData = [
    { sno: 1, contractName: "A PROJECTS WORK", tenderRefNo: "EST/39", contractValue: "380700.00" },
    { sno: 2, contractName: "road test", tenderRefNo: "EST/41", contractValue: "154378.00" },
    { sno: 3, contractName: "TESTING ENTRY FOR Returnable item", tenderRefNo: "EST/42", contractValue: "10000.00" },
    { sno: 4, contractName: "CBE to MTP Batch Work Part 2", tenderRefNo: "EST/3", contractValue: "5773.50" },
    { sno: 5, contractName: "KOVAI TO HOPES", tenderRefNo: "EST/46", contractValue: "6596175.00" },
    { sno: 6, contractName: "D HIGHWAY CONSTRUCTION", tenderRefNo: "EST/47", contractValue: "1503000.00" },
    { sno: 7, contractName: "test", tenderRefNo: "EST/1045", contractValue: "174455.00" },
    { sno: 8, contractName: "ROAD LAYER", tenderRefNo: "EST/1046", contractValue: "17500.00" },
    { sno: 9, contractName: "Test Road Project 05-02-2022", tenderRefNo: "EST/1047", contractValue: "21670674.24" },
    { sno: 10, contractName: "ROAD INFRA", tenderRefNo: "EST/2065", contractValue: "88500.00" },
    { sno: 11, contractName: "Building Work(14/05/2022)", tenderRefNo: "EST/2066", contractValue: "46365.00" },
    { sno: 12, contractName: "Site preparation", tenderRefNo: "EST/2072", contractValue: "2320000.00" },
    { sno: 13, contractName: "TEST-MAIN", tenderRefNo: "EST/2073", contractValue: "62814.00" }
  ];

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    toast.success("Search filters applied!");
  };

  const handleExport = () => {
    toast.success("Report exported successfully!");
  };

  const handlePrint = () => {
    toast.success("Report sent to printer!");
  };

  const handleViewDetails = (tenderRefNo) => {
    toast.info(`Viewing details for ${tenderRefNo}`);
  };

  // Filter data based on search term
  const filteredData = tenderData.filter(item =>
    item.contractName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.tenderRefNo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate total contract value
  const totalValue = filteredData.reduce((sum, item) => sum + parseFloat(item.contractValue), 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  return (
    <ProjectPostOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">TENDER SUCCESSFUL REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black"
        }`}>
          {/* Header Section */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <FaTrophy className={`text-2xl ${isDark ? "text-yellow-400" : "text-yellow-600"}`} />
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Tender Successful Report
              </h1>
            </div>

            <div className="flex items-center gap-3">
              {/* Search Bar */}
              <div className="relative">
                <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${
                  isDark ? "text-gray-400" : "text-gray-500"
                }`} />
                <input
                  type="text"
                  placeholder="Search tenders..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className={`pl-10 pr-4 py-2 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                    isDark 
                      ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" 
                      : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                  }`}
                />
              </div>

              {/* Action Buttons */}
              <button
                onClick={handleExport}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : "bg-green-500 hover:bg-green-600 text-white"
                }`}
              >
                <FaDownload />
                Export
              </button>

              <button
                onClick={handlePrint}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-purple-600 hover:bg-purple-700 text-white"
                    : "bg-purple-500 hover:bg-purple-600 text-white"
                }`}
              >
                <FaPrint />
                Print
              </button>

              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {/* Filter Section */}
          {showFilters && (
            <div className={`p-6 rounded-lg mb-6 ${
              isDark ? "bg-gray-800/50" : "bg-yellow-50"
            }`}>
              <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                isDark ? "text-white" : "text-yellow-900"
              }`}>
                <FaFilter />
                Filter Options
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    From Date
                  </label>
                  <input
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all ${
                      isDark 
                        ? "bg-gray-700 border-gray-600 text-white" 
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    To Date
                  </label>
                  <input
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => handleFilterChange('toDate', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all ${
                      isDark 
                        ? "bg-gray-700 border-gray-600 text-white" 
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    Contract Name
                  </label>
                  <input
                    type="text"
                    placeholder="Enter contract name"
                    value={filters.contractName}
                    onChange={(e) => handleFilterChange('contractName', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all ${
                      isDark 
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" 
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    Tender Ref No
                  </label>
                  <input
                    type="text"
                    placeholder="EST/..."
                    value={filters.tenderRefNo}
                    onChange={(e) => handleFilterChange('tenderRefNo', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all ${
                      isDark 
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" 
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    Min Value
                  </label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={filters.minValue}
                    onChange={(e) => handleFilterChange('minValue', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all ${
                      isDark 
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" 
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}>
                    Max Value
                  </label>
                  <input
                    type="number"
                    placeholder="0.00"
                    value={filters.maxValue}
                    onChange={(e) => handleFilterChange('maxValue', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all ${
                      isDark 
                        ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400" 
                        : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                    }`}
                  />
                </div>
              </div>

              <div className="flex justify-center">
                <button
                  onClick={handleSearch}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark ? `${buttonClass} ${hoverClass} text-white` : `${buttonClass} ${hoverClass} text-white`
                  }`}
                >
                  <FaSearch />
                  Apply Filters
                </button>
              </div>
            </div>
          )}

          {/* Summary Statistics */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className={`p-4 rounded-lg border ${
              isDark 
                ? "bg-yellow-900/20 border-yellow-700" 
                : "bg-yellow-50 border-yellow-200"
            }`}>
              <div className="text-center">
                <h3 className={`text-lg font-semibold ${
                  isDark ? "text-yellow-300" : "text-yellow-900"
                }`}>
                  Successful Tenders
                </h3>
                <p className={`text-2xl font-bold ${
                  isDark ? "text-white" : "text-yellow-600"
                }`}>
                  {filteredData.length}
                </p>
              </div>
            </div>

            <div className={`p-4 rounded-lg border ${
              isDark 
                ? "bg-green-900/20 border-green-700" 
                : "bg-green-50 border-green-200"
            }`}>
              <div className="text-center">
                <h3 className={`text-lg font-semibold ${
                  isDark ? "text-green-300" : "text-green-900"
                }`}>
                  Total Value
                </h3>
                <p className={`text-xl font-bold ${
                  isDark ? "text-white" : "text-green-600"
                }`}>
                  ₹{formatCurrency(totalValue)}
                </p>
              </div>
            </div>

            <div className={`p-4 rounded-lg border ${
              isDark 
                ? "bg-blue-900/20 border-blue-700" 
                : "bg-blue-50 border-blue-200"
            }`}>
              <div className="text-center">
                <h3 className={`text-lg font-semibold ${
                  isDark ? "text-blue-300" : "text-blue-900"
                }`}>
                  Avg Value
                </h3>
                <p className={`text-xl font-bold ${
                  isDark ? "text-white" : "text-blue-600"
                }`}>
                  ₹{formatCurrency(totalValue / filteredData.length || 0)}
                </p>
              </div>
            </div>

            <div className={`p-4 rounded-lg border ${
              isDark 
                ? "bg-purple-900/20 border-purple-700" 
                : "bg-purple-50 border-purple-200"
            }`}>
              <div className="text-center">
                <h3 className={`text-lg font-semibold ${
                  isDark ? "text-purple-300" : "text-purple-900"
                }`}>
                  Success Rate
                </h3>
                <p className={`text-xl font-bold flex items-center justify-center gap-2 ${
                  isDark ? "text-white" : "text-purple-600"
                }`}>
                  <FaAward />
                  100%
                </p>
              </div>
            </div>
          </div>

          {/* Data Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                      S.no
                    </th>
                    <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                      Contract Name
                    </th>
                    <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                      Tender RefNo
                    </th>
                    <th className="px-6 py-4 text-center font-semibold border-r border-white/20">
                      Contract Value
                    </th>
                    <th className="px-6 py-4 text-center font-semibold">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${
                  isDark ? "divide-gray-700" : "divide-gray-200"
                }`}>
                  {filteredData.map((item) => (
                    <tr key={item.sno} className={`transition-colors duration-200 ${
                      isDark ? "bg-gray-800 hover:bg-gray-750" : "bg-white hover:bg-gray-50"
                    }`}>
                      <td className={`px-6 py-4 text-center border-r ${
                        isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"
                      }`}>
                        {item.sno}
                      </td>
                      <td className={`px-6 py-4 text-left border-r ${
                        isDark ? "border-gray-700 text-white" : "border-gray-200 text-gray-900"
                      }`}>
                        <div className="max-w-xs truncate" title={item.contractName}>
                          {item.contractName}
                        </div>
                      </td>
                      <td className={`px-6 py-4 text-center border-r font-medium ${
                        isDark ? "border-gray-700 text-blue-300" : "border-gray-200 text-blue-600"
                      }`}>
                        <div className="flex items-center justify-center gap-2">
                          <FaTrophy className="text-yellow-500 text-sm" />
                          {item.tenderRefNo}
                        </div>
                      </td>
                      <td className={`px-6 py-4 text-right border-r font-semibold ${
                        isDark ? "border-gray-700 text-green-300" : "border-gray-200 text-green-600"
                      }`}>
                        ₹{formatCurrency(parseFloat(item.contractValue))}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleViewDetails(item.tenderRefNo)}
                          className={`px-3 py-1 rounded-lg text-xs font-medium transition-all duration-200 hover:scale-105 ${
                            isDark 
                              ? "bg-yellow-600 hover:bg-yellow-700 text-white" 
                              : "bg-yellow-100 hover:bg-yellow-200 text-yellow-700"
                          }`}
                        >
                          <FaEye className="inline mr-1" />
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className={`${buttonClass} text-white`}>
                  <tr>
                    <td colSpan="3" className="px-6 py-4 text-center font-bold border-r border-white/20">
                      TOTAL SUCCESSFUL
                    </td>
                    <td className="px-6 py-4 text-right font-bold border-r border-white/20">
                      ₹{formatCurrency(totalValue)}
                    </td>
                    <td className="px-6 py-4"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Results Info */}
          <div className="mt-4 flex justify-between items-center">
            <div className={`text-sm flex items-center gap-2 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              <FaTrophy className="text-yellow-500" />
              Showing {filteredData.length} successful tenders
            </div>
            <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
              Report generated: {new Date().toLocaleString()}
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPostOrderLayoutWrapper>
  );
};

export default TenderSuccessfulReport;